import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../store";
import { creditNoteActions } from "../slices/creditNote";
import { CreditNoteMapper } from "../../mappers/CreditNoteMapper";
import { getErrorMessage } from "../../utils/errorUtils";

import { CreditNoteRequestDto } from "../../shared/dtos/creditNote/CreditNoteRequestDto";
import { CreditNoteResponseDto } from "../../shared/dtos/creditNote/CreditNoteResponseDto";
import { CreditNoteSummaryDto } from "../../shared/dtos/creditNote/CreditNoteSummaryDto";
import { DateRangePaginationQueryDto } from "../../shared/dtos/common/DateRangePaginationQueryDto";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getCreditNotes =
  (params?: DateRangePaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(creditNoteActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<CreditNoteSummaryDto>>
      >("/credit-notes", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(creditNoteActions.setItems(data.data.content));
        dispatch(creditNoteActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(creditNoteActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getCreditNoteById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(creditNoteActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CreditNoteResponseDto>
      >(`/credit-notes/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(creditNoteActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(creditNoteActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createCreditNote =
  (payload: CreditNoteRequestDto) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(creditNoteActions.setLoadingCreate());
    try {
      const { currentShift } = getState().shift;
      const finalPayload = {
        ...payload,
        shiftId: currentShift?.id || payload.shiftId,
      };

      const { data } = await axiosInstance.post<
        ApiResponseDto<CreditNoteResponseDto>
      >("/credit-notes", finalPayload, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Credit note created successfully!");
        const summary = CreditNoteMapper.mapCreditNoteToSummary(data.data);
        if (summary) dispatch(creditNoteActions.createSuccess(summary));
        dispatch(
          creditNoteActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(creditNoteActions.setErrorCreate(message));
    }
  };

export const applyToInvoice =
  (id: string, invoiceId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(creditNoteActions.setLoadingApply());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<CreditNoteResponseDto>
      >(`/credit-notes/${id}/apply-to-invoice/${invoiceId}`, null, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Applied to invoice successfully!");
        dispatch(creditNoteActions.actionSuccess(data.data));
        dispatch(
          creditNoteActions.setServerStatus({
            operation: "apply",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(creditNoteActions.setErrorApply(message));
    }
  };

export const voidCreditNote =
  (id: string, reason: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(creditNoteActions.setLoadingVoid());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/credit-notes/void/${id}?reason=${encodeURIComponent(reason)}`,
        null,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Credit Note voided!");
        dispatch(
          creditNoteActions.setServerStatus({
            operation: "void",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(creditNoteActions.setErrorVoid(message));
    }
  };

export const getCreditNotePdf =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(creditNoteActions.setLoadingPdf());
    try {
      const { data } = await axiosInstance.get<Blob>(
        `/credit-notes/pdf/${id}`,
        {
          headers: { requiresAuth: true },
          responseType: "blob",
        },
      );

      const pdfBlobUrl = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );
      dispatch(creditNoteActions.setPdfUrl(pdfBlobUrl));
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error("Failed to load PDF preview: " + message);
      dispatch(creditNoteActions.setErrorPdf(message));
    }
  };

export const emailCreditNote =
  (id: string, emailPayload: EmailRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(creditNoteActions.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/credit-notes/email/${id}`,
        emailPayload,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Credit Note emailed successfully!");
        dispatch(creditNoteActions.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(creditNoteActions.setErrorEmail(message));
      return false;
    }
  };

export const resetCreditNoteServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(creditNoteActions.setServerStatus(null));
  };
